// Import styles
import './styles.css';

// Import Sentry
import * as Sentry from '@sentry/browser';

// Make Sentry available globally
window.Sentry = Sentry;

// Initialize Sentry
Sentry.init({
  dsn: "https://f0b0e4520679bdbc4440a93774087d95@o4509441617690624.ingest.us.sentry.io/4509442125529088",
  tracesSampleRate: 1.0,
  environment: import.meta.env.MODE || 'production'
});

// Log page startup
console.log('BetterKey frontend is running!');

const COMPATIBILITY_API_PATH = '/api/compatibility';
const COMPATIBILITY_API_SUPPORTED_ONLY = true;
const COMPATIBILITY_REGION_STORAGE_KEY = 'betterkey.compatibilityRegionOverride';
const COMPATIBILITY_CAPABILITIES = [
  { value: 'lock-unlock', label: 'Lock & unlock', sourceField: 'lockUnlockSupported' },
  { value: 'windows', label: 'Windows', sourceField: 'windowsSupported' }
];
const COMPATIBILITY_REGIONS = [
  { value: 'us', label: 'United States' },
  { value: 'ca', label: 'Canada' },
  { value: 'europe', label: 'Europe' }
];
const COMPATIBILITY_EUROPE_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'GB', 'IS', 'LI', 'NO', 'CH'
]);
const US_PRIORITY_MAKES = [
  'Toyota',
  'Honda',
  'Ford',
  'Chevrolet',
  'Nissan',
  'Hyundai',
  'Kia',
  'Subaru',
  'Mazda',
  'Volkswagen',
  'Tesla',
  'BMW',
  'Mercedes-Benz',
  'Audi',
  'Lexus',
  'Jeep',
  'Ram',
  'GMC'
];
const TRIM_SUFFIX_TOKENS = new Set([
  'base', 'sport', 'limited', 'premium', 'platinum', 'touring', 'signature', 'performance',
  'ultimate', 'special', 'edition', 'type', 'se', 'sel', 'xle', 'xlt', 'xl', 'lt', 'ls', 'lx',
  'ex', 'exl', 'sx', 'gt', 'gts', 'amg', 'xdrive', 'sdrive', 'quattro', '4matic', 'hev',
  'phev', 'hybrid', 'electric', 'ev', 'bev', 'awd', 'fwd', 'rwd', 'long', 'range', 'max',
  'prime', 'plus', 'pro', 'ultra', 'denali', 'trailhawk', 'trd', 'zr2', 'rs', 'ss', 'st',
  'sv', 'sl', 'sr', 'sr5', 'plaid', 'wilderness', 'trx', 'rho', 'sti', 'diesel', 'battery',
  'recaro', 'package', 'exclusive', 'series', 'high', 'low', 'med', 'medium', 'roof', 'pass'
]);
const BODY_STYLE_TOKENS = new Set([
  'sedan', 'suv', 'coupe', 'convertible', 'hatchback', 'wagon', 'van', 'pickup', 'truck',
  'cabriolet', 'roadster', 'crossover', 'minivan', 'crew', 'cab', 'chassis', 'sportback',
  'gran', 'turismo', 'estate', 'liftback', 'fastback', 'passenger', 'cargo'
]);
const MODEL_NORMALIZATION_TOKENS = new Set([
  ...TRIM_SUFFIX_TOKENS,
  ...BODY_STYLE_TOKENS,
  'all', 'variant', 'variants', 'with', 'without', 'plug', 'in', 'plugin',
  'allwheel', 'wheel', 'drive'
]);
const NHTSA_TRIM_NOISE_PATTERNS = [
  /\d/,
  /^[afrx]?wd$/i,
  /^(early|earlier|later|previous|new|release|edition|version)$/i,
  /^seat$/i,
  /^(base|standard|long|range|plus|performance|sport|luxury|premium|launch|founders?|signature)$/i
];
const COMPATIBILITY_MODEL_NORMALIZATION = __COMPATIBILITY_MODEL_NORMALIZATION__;
const COMPATIBILITY_MODEL_SUPPRESSIONS_BY_MAKE = new Map(
  Object.entries(COMPATIBILITY_MODEL_NORMALIZATION.suppressionsByMake || {}).map(([make, names]) => [make, new Set(names)])
);
const COMPATIBILITY_MODEL_ALIASES_BY_MAKE = new Map(
  Object.entries(COMPATIBILITY_MODEL_NORMALIZATION.aliasesByMake || {}).map(([make, aliases]) => [make, new Map(Object.entries(aliases))])
);

function normalizeMakeMatchKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function isCompatibilityRegion(value) {
  return COMPATIBILITY_REGIONS.some((region) => region.value === value);
}

function regionFromCountryCode(code) {
  const normalized = String(code || '').trim().toUpperCase();
  if (normalized === 'US') return 'us';
  if (normalized === 'CA') return 'ca';
  if (COMPATIBILITY_EUROPE_COUNTRY_CODES.has(normalized)) return 'europe';
  return null;
}

function extractRegionCodeFromLocale(localeValue) {
  const locale = String(localeValue || '').trim();
  if (!locale) return null;

  if (typeof Intl !== 'undefined' && typeof Intl.Locale === 'function') {
    try {
      const region = new Intl.Locale(locale).region;
      if (region) return region.toUpperCase();
    } catch (_error) {
      // Fall back to manual parsing when Intl.Locale cannot parse the input.
    }
  }

  const parts = locale.replace(/_/g, '-').split('-').filter(Boolean);
  for (const part of parts) {
    if (/^[A-Za-z]{2}$/.test(part) && part.toUpperCase() === part) {
      return part;
    }
  }

  if (parts.length >= 2) {
    const candidate = parts[parts.length - 1];
    if (/^[A-Za-z]{2}$/.test(candidate)) {
      return candidate.toUpperCase();
    }
  }

  return null;
}

function inferCompatibilityRegionFromBrowser() {
  const localeCandidates = [];
  if (Array.isArray(navigator.languages)) {
    localeCandidates.push(...navigator.languages);
  }
  if (navigator.language) {
    localeCandidates.push(navigator.language);
  }

  for (const locale of localeCandidates) {
    const regionCode = extractRegionCodeFromLocale(locale);
    const region = regionFromCountryCode(regionCode);
    if (region) return region;
  }

  return 'us';
}

function getStoredCompatibilityRegion() {
  try {
    const stored = window.localStorage.getItem(COMPATIBILITY_REGION_STORAGE_KEY);
    return isCompatibilityRegion(stored) ? stored : null;
  } catch (_error) {
    return null;
  }
}

function setStoredCompatibilityRegion(region) {
  try {
    if (isCompatibilityRegion(region)) {
      window.localStorage.setItem(COMPATIBILITY_REGION_STORAGE_KEY, region);
    } else {
      window.localStorage.removeItem(COMPATIBILITY_REGION_STORAGE_KEY);
    }
  } catch (_error) {
    // Ignore storage failures; locale inference will still work.
  }
}

function resolveCompatibilityRegion() {
  return getStoredCompatibilityRegion() || inferCompatibilityRegionFromBrowser();
}

function normalizeModelNameKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function cleanCompatibilityModelName(makeName, modelName) {
  let cleanedName = String(modelName || '').trim();
  if (!cleanedName) return null;

  cleanedName = cleanedName.replace(/^redundant\s+/i, '').trim();
  if (!cleanedName) return null;

  const makeKey = normalizeMakeMatchKey(makeName);
  const normalizedName = normalizeModelNameKey(cleanedName);

  const suppressedNames = COMPATIBILITY_MODEL_SUPPRESSIONS_BY_MAKE.get(makeKey);
  if (suppressedNames?.has(normalizedName)) {
    return null;
  }

  const aliases = COMPATIBILITY_MODEL_ALIASES_BY_MAKE.get(makeKey);
  return aliases?.get(normalizedName) || cleanedName;
}

function normalizeModelToken(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9+-]/g, '');
}

function splitModelTokens(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

function resetSelect(selectElement, placeholder, disabled) {
  selectElement.innerHTML = '';
  const option = document.createElement('option');
  option.value = '';
  option.textContent = placeholder;
  option.selected = true;
  selectElement.appendChild(option);
  selectElement.disabled = disabled;
}

function prettyToken(token) {
  const upperTokens = new Set([
    'ev', 'bev', 'hev', 'phev', 'gt', 'gts', 'amg', 'rs', 'ss', 'st', 'sv',
    'sl', 'sr', 'xle', 'xlt', 'xl', 'lt', 'lx', 'ex', 'exl', 'trd', 'zr2', 'awd',
    'fwd', 'rwd', 'suv'
  ]);
  if (upperTokens.has(token)) return token.toUpperCase();
  if (/[0-9]/.test(token)) return token.toUpperCase();
  return token.charAt(0).toUpperCase() + token.slice(1);
}

function compareVehicleTypes(a, b) {
  const normalizedA = String(a || '').trim().toUpperCase();
  const normalizedB = String(b || '').trim().toUpperCase();
  const vehicleTypePriority = ['ICE', 'HEV', 'PHEV', 'BEV'];
  const aRank = vehicleTypePriority.indexOf(normalizedA);
  const bRank = vehicleTypePriority.indexOf(normalizedB);
  const normalizedARank = aRank === -1 ? vehicleTypePriority.length : aRank;
  const normalizedBRank = bRank === -1 ? vehicleTypePriority.length : bRank;
  if (normalizedARank !== normalizedBRank) return normalizedARank - normalizedBRank;
  return normalizedA.localeCompare(normalizedB);
}

function summarizeVehicleTypes(vehicleTypes) {
  const uniqueTypes = Array.from(new Set(Array.from(vehicleTypes || [])
    .map((vehicleType) => String(vehicleType || '').trim().toUpperCase())
    .filter(Boolean)))
    .sort(compareVehicleTypes);
  if (uniqueTypes.length === 0) return null;
  if (uniqueTypes.length === 1) return uniqueTypes[0];
  return uniqueTypes.join(' / ');
}

function stripNhtsaModelNameNoise(name) {
  let result = String(name || '')
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  for (let i = 0; i < 3; i += 1) {
    const stripped = result
      .replace(/\s+(?:BEV|PHEV|HEV|FCEV|HYBRID|ELECTRIC|HYDROGEN|EV)$/i, '')
      .trim();
    if (stripped === result || stripped.length === 0) break;
    result = stripped;
  }

  return result || String(name || '').trim();
}

function isNhtsaTrimNoiseToken(token) {
  if (!token) return false;
  return NHTSA_TRIM_NOISE_PATTERNS.some((pattern) => pattern.test(token));
}

function trailingNhtsaTokensAreAllNoise(tokens) {
  return Array.isArray(tokens) && tokens.length > 0 && tokens.every(isNhtsaTrimNoiseToken);
}

function findRedundantNhtsaBaseModelName(modelName, baseModelKeys) {
  const normalizedName = normalizeModelNameKey(stripNhtsaModelNameNoise(modelName));
  const tokens = splitModelTokens(normalizedName);

  for (let prefixLength = tokens.length - 1; prefixLength >= 1; prefixLength -= 1) {
    const prefixKey = tokens.slice(0, prefixLength).join(' ');
    if (!baseModelKeys.has(prefixKey)) continue;
    if (!trailingNhtsaTokensAreAllNoise(tokens.slice(prefixLength))) continue;
    return prefixKey;
  }

  return null;
}

function buildCanonicalModelParts(makeName, modelName) {
  let tokens = splitModelTokens(modelName);
  const makeTokens = splitModelTokens(makeName);
  const normalizedMake = normalizeMakeMatchKey(makeName);

  // Remove make prefix duplication (e.g., "BMW 3 Series").
  while (makeTokens.length > 0 && tokens.length > 0 && tokens[0] === makeTokens[0]) {
    tokens.shift();
    makeTokens.shift();
  }

  // Normalize letter+number model IDs (e.g., "f 150" -> "f150").
  const combined = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const current = tokens[i];
    const next = tokens[i + 1];
    if (/^[a-z]$/.test(current) && /^[0-9]{2,4}$/.test(next || '')) {
      combined.push(`${current}${next}`);
      i += 1;
      continue;
    }
    combined.push(current);
  }
  tokens = combined;

  // Handle known naming transitions that appear as legacy prefixes in source datasets.
  if (normalizedMake === 'subaru') {
    if (tokens.length >= 2 && tokens[0] === 'xv' && tokens[1] === 'crosstrek') {
      tokens = tokens.slice(1);
    } else if (tokens.length === 1 && tokens[0] === 'xv') {
      tokens = ['crosstrek'];
    }
  }

  // Drop trailing trim/body/powertrain descriptors.
  while (tokens.length > 1 && MODEL_NORMALIZATION_TOKENS.has(tokens[tokens.length - 1])) {
    tokens.pop();
  }

  if (tokens.length === 0) {
    tokens = splitModelTokens(modelName);
  }

  const canonicalKey = tokens.join(' ');
  const displayName = tokens.map(prettyToken).join(' ');
  return { canonicalKey, displayName };
}

function parseYearsText(yearsText) {
  const years = new Set();
  if (!yearsText) return years;

  const parts = String(yearsText)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  parts.forEach((part) => {
    const rangeMatch = part.match(/^(\d{4})\s*-\s*(\d{4})$/);
    if (rangeMatch) {
      const start = Number(rangeMatch[1]);
      const end = Number(rangeMatch[2]);
      if (Number.isFinite(start) && Number.isFinite(end)) {
        const low = Math.min(start, end);
        const high = Math.max(start, end);
        for (let year = low; year <= high; year += 1) {
          years.add(year);
        }
      }
      return;
    }

    const singleMatch = part.match(/^(\d{4})$/);
    if (singleMatch) {
      years.add(Number(singleMatch[1]));
    }
  });

  return years;
}

function formatYearsSet(yearSet) {
  const sortedYears = Array.from(yearSet).sort((a, b) => a - b);
  if (sortedYears.length === 0) return null;

  const ranges = [];
  let start = sortedYears[0];
  let previous = sortedYears[0];

  for (let i = 1; i < sortedYears.length; i += 1) {
    const year = sortedYears[i];
    if (year === previous + 1) {
      previous = year;
      continue;
    }
    ranges.push(start === previous ? String(start) : `${start}-${previous}`);
    start = year;
    previous = year;
  }

  ranges.push(start === previous ? String(start) : `${start}-${previous}`);
  return ranges.join(', ');
}

function getMaxYear(yearSet) {
  if (!yearSet || yearSet.size === 0) return null;
  let maxYear = null;
  yearSet.forEach((year) => {
    if (!Number.isFinite(year)) return;
    if (maxYear === null || year > maxYear) {
      maxYear = year;
    }
  });
  return maxYear;
}

function getMinYear(yearSet) {
  if (!yearSet || yearSet.size === 0) return null;
  let minYear = null;
  yearSet.forEach((year) => {
    if (!Number.isFinite(year)) return;
    if (minYear === null || year < minYear) {
      minYear = year;
    }
  });
  return minYear;
}

function formatCompatibleYears(yearSet) {
  const minYear = getMinYear(yearSet);
  if (minYear === null) return null;
  return `${minYear}+`;
}

function isTrimSuffixToken(token) {
  const normalized = normalizeModelToken(token);
  if (!normalized) return true;
  if (BODY_STYLE_TOKENS.has(normalized)) return true;
  if (TRIM_SUFFIX_TOKENS.has(normalized)) return true;
  if (/\d/.test(normalized)) return true;
  if (normalized.length <= 2) return true;
  return false;
}

function getCollapsedBaseModelName(modelName, allModelNamesSet) {
  const words = String(modelName || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return modelName;

  for (let removedWordCount = 1; removedWordCount < words.length; removedWordCount += 1) {
    const prefixWords = words.slice(0, words.length - removedWordCount);
    const suffixWords = words.slice(words.length - removedWordCount);
    const prefixName = prefixWords.join(' ');

    if (!allModelNamesSet.has(normalizeModelNameKey(prefixName))) continue;
    if (!suffixWords.every(isTrimSuffixToken)) continue;
    return prefixName;
  }

  return modelName;
}

const COMPATIBILITY_ORIGIN = Object.freeze({
  SMARTCAR: 'smartcar',
  NHTSA_ONLY: 'nhtsa-only'
});

function createCollapsedModelGroup(name, vehicleType = null) {
  return {
    name,
    vehicleType,
    compatibleYears: new Set(),
    incompatibleYears: new Set(),
    newYears: new Set(),
    compatibleVehicleTypes: new Set(),
    incompatibleVehicleTypes: new Set(),
    newVehicleTypes: new Set(),
    compatibleCount: 0,
    incompatibleCount: 0,
    newCount: 0,
    capabilitySupport: Object.fromEntries(COMPATIBILITY_CAPABILITIES.map((capability) => [capability.value, false]))
  };
}

function getModelCapabilitySupport(model, capability) {
  if (capability.value !== 'lock-unlock') {
    return model[capability.sourceField] === true;
  }

  const granularFields = ['lockStateSupported', 'lockCommandSupported', 'unlockCommandSupported'];
  const hasGranularEvidence = granularFields.some((field) =>
    Object.prototype.hasOwnProperty.call(model, field)
  );
  if (!hasGranularEvidence) {
    return model.lockUnlockSupported === true;
  }

  const values = granularFields.map((field) => model[field]);
  if (values.some((value) => value === false)) return false;
  if (values.every((value) => value === true)) return true;
  return null;
}

function mergeCapabilitySupport(current, incoming) {
  if (current === true || incoming === true) return true;
  if (current === null || incoming === null) return null;
  return false;
}

function isAnyCapabilitySupported(model) {
  return COMPATIBILITY_CAPABILITIES.some((capability) =>
    getModelCapabilitySupport(model, capability) === true
  );
}

function isAnyCapabilityUnknown(model) {
  return COMPATIBILITY_CAPABILITIES.some((capability) =>
    getModelCapabilitySupport(model, capability) === null
  );
}

function addModelToCollapsedGroup(group, model) {
  const modelYears = parseYearsText(model.years);
  for (const capability of COMPATIBILITY_CAPABILITIES) {
    group.capabilitySupport[capability.value] = mergeCapabilitySupport(
      group.capabilitySupport[capability.value],
      getModelCapabilitySupport(model, capability)
    );
  }
  if (isAnyCapabilitySupported(model)) {
    modelYears.forEach((year) => group.compatibleYears.add(year));
    if (model.vehicleType) group.compatibleVehicleTypes.add(String(model.vehicleType));
    group.compatibleCount += 1;
  } else if (model.origin === COMPATIBILITY_ORIGIN.NHTSA_ONLY || isAnyCapabilityUnknown(model)) {
    modelYears.forEach((year) => group.newYears.add(year));
    if (model.vehicleType) group.newVehicleTypes.add(String(model.vehicleType));
    group.newCount += 1;
  } else {
    modelYears.forEach((year) => group.incompatibleYears.add(year));
    if (model.vehicleType) group.incompatibleVehicleTypes.add(String(model.vehicleType));
    group.incompatibleCount += 1;
  }
}

function finalizeCollapsedModelGroup(group) {
  const finalized = {
    ...group,
    compatibleYears: new Set(group.compatibleYears),
    incompatibleYears: new Set(group.incompatibleYears),
    newYears: new Set(group.newYears)
  };

  const minCompatibleYear = getMinYear(finalized.compatibleYears);
  if (minCompatibleYear !== null && finalized.incompatibleYears.size > 0) {
    Array.from(finalized.incompatibleYears).forEach((year) => {
      if (year >= minCompatibleYear) {
        finalized.incompatibleYears.delete(year);
      }
    });
  }

  return finalized;
}

function buildVariantSignature(group) {
  const compatibleYears = formatYearsSet(group.compatibleYears) || '';
  const incompatibleYears = formatYearsSet(group.incompatibleYears) || '';
  return `${compatibleYears}|${incompatibleYears}`;
}

function buildRowsFromCollapsedGroup(group, displayName, subtitle = '') {
  const rows = [];
  const minCompatibleYear = getMinYear(group.compatibleYears);
  const explicitSubtitle = String(subtitle || '').trim().toUpperCase();
  const compatibleSubtitle = explicitSubtitle || summarizeVehicleTypes(group.compatibleVehicleTypes);
  const incompatibleSubtitle = explicitSubtitle || summarizeVehicleTypes(group.incompatibleVehicleTypes);
  const newSubtitle = explicitSubtitle || summarizeVehicleTypes(group.newVehicleTypes);
  const capabilitySupport = { ...group.capabilitySupport };

  if (group.compatibleCount > 0) {
    rows.push({
      name: displayName,
      subtitle: compatibleSubtitle || '',
      years: formatCompatibleYears(group.compatibleYears),
      openEndedFromYear: minCompatibleYear,
      vehicleType: group.compatibleVehicleTypes.size === 1
        ? Array.from(group.compatibleVehicleTypes)[0]
        : summarizeVehicleTypes(group.compatibleVehicleTypes),
      isCompatible: true,
      capabilitySupport
    });
  }
  if (group.newCount > 0 && group.newYears.size > 0) {
    rows.push({
      name: displayName,
      subtitle: newSubtitle || '',
      years: formatYearsSet(group.newYears),
      vehicleType: group.newVehicleTypes.size === 1
        ? Array.from(group.newVehicleTypes)[0]
        : summarizeVehicleTypes(group.newVehicleTypes),
      isCompatible: false,
      isNew: true,
      capabilitySupport
    });
  }
  if (group.incompatibleCount > 0) {
    if (group.incompatibleYears.size === 0) {
      return rows;
    }
    const maxIncompatibleYear = getMaxYear(group.incompatibleYears);
    const isOnlyPre2015 = maxIncompatibleYear !== null && maxIncompatibleYear < 2015;
    if (isOnlyPre2015) {
      return rows;
    }
    rows.push({
      name: displayName,
      subtitle: incompatibleSubtitle || '',
      years: formatYearsSet(group.incompatibleYears),
      vehicleType: group.incompatibleVehicleTypes.size === 1
        ? Array.from(group.incompatibleVehicleTypes)[0]
        : summarizeVehicleTypes(group.incompatibleVehicleTypes),
      isCompatible: false,
      capabilitySupport
    });
  }
  return rows;
}

function collapseMakeModels(makeName, models) {
  const rawModels = Array.isArray(models) ? models : [];
  const cleanedModels = rawModels
    .map((model) => {
      const cleanedName = cleanCompatibilityModelName(makeName, model.name);
      if (!cleanedName) return null;
      if (cleanedName === model.name) return model;
      return { ...model, name: cleanedName };
    })
    .filter(Boolean);
  const allModelNamesSet = new Set(cleanedModels.map((model) => normalizeModelNameKey(model.name)));
  const suppressibleBaseModelKeys = new Set(
    cleanedModels
      .filter((model) => model.origin !== COMPATIBILITY_ORIGIN.NHTSA_ONLY)
      .flatMap((model) => {
        const baseName = getCollapsedBaseModelName(model.name, allModelNamesSet);
        const canonical = buildCanonicalModelParts(makeName, baseName);
        return [
          normalizeModelNameKey(model.name),
          normalizeModelNameKey(baseName),
          normalizeModelNameKey(canonical.canonicalKey || baseName)
        ].filter(Boolean);
      })
  );
  const baseGroups = new Map();

  cleanedModels.forEach((model) => {
    // Keep genuinely distinct NHTSA-only trims (e.g. "Outback Wilderness"),
    // but suppress filing-noise pseudo-variants when a real base model already
    // exists (e.g. "Model S (all Variants) Later Release", "Model X 6-Seat").
    // Remaining NHTSA-only rows still bypass the prefix-collapser so they keep
    // their distinct trim labels instead of being merged away.
    const isNhtsaOnly = model.origin === COMPATIBILITY_ORIGIN.NHTSA_ONLY;
    if (isNhtsaOnly && findRedundantNhtsaBaseModelName(model.name, suppressibleBaseModelKeys)) {
      return;
    }
    const baseName = isNhtsaOnly
      ? model.name
      : getCollapsedBaseModelName(model.name, allModelNamesSet);
    const canonical = isNhtsaOnly
      ? { canonicalKey: normalizeModelNameKey(baseName), displayName: baseName }
      : buildCanonicalModelParts(makeName, baseName);
    const baseKey = normalizeModelNameKey(canonical.canonicalKey || baseName);
    const normalizedVehicleType = String(model.vehicleType || '').trim().toUpperCase();

    if (!baseGroups.has(baseKey)) {
      baseGroups.set(baseKey, {
        displayName: canonical.displayName || baseName,
        variants: new Map()
      });
    }

    const baseGroup = baseGroups.get(baseKey);
    const variantKey = normalizedVehicleType || 'unknown';
    if (!baseGroup.variants.has(variantKey)) {
      baseGroup.variants.set(
        variantKey,
        createCollapsedModelGroup(baseGroup.displayName, normalizedVehicleType || null)
      );
    }

    addModelToCollapsedGroup(baseGroup.variants.get(variantKey), model);
  });

  return Array.from(baseGroups.values())
    .flatMap((baseGroup) => {
      const variants = Array.from(baseGroup.variants.values())
        .map(finalizeCollapsedModelGroup)
        .sort((a, b) => compareVehicleTypes(a.vehicleType, b.vehicleType));

      const shouldSplitByVehicleType =
        variants.length > 1 && new Set(variants.map(buildVariantSignature)).size > 1;
      if (!shouldSplitByVehicleType) {
        const mergedGroup = createCollapsedModelGroup(baseGroup.displayName, null);
        variants.forEach((variant) => {
          variant.compatibleYears.forEach((year) => mergedGroup.compatibleYears.add(year));
          variant.incompatibleYears.forEach((year) => mergedGroup.incompatibleYears.add(year));
          variant.newYears.forEach((year) => mergedGroup.newYears.add(year));
          variant.compatibleVehicleTypes.forEach((vehicleType) => mergedGroup.compatibleVehicleTypes.add(vehicleType));
          variant.incompatibleVehicleTypes.forEach((vehicleType) => mergedGroup.incompatibleVehicleTypes.add(vehicleType));
          variant.newVehicleTypes.forEach((vehicleType) => mergedGroup.newVehicleTypes.add(vehicleType));
          mergedGroup.compatibleCount += variant.compatibleCount;
          mergedGroup.incompatibleCount += variant.incompatibleCount;
          mergedGroup.newCount += variant.newCount;
          for (const capability of COMPATIBILITY_CAPABILITIES) {
            mergedGroup.capabilitySupport[capability.value] = mergeCapabilitySupport(
              mergedGroup.capabilitySupport[capability.value],
              variant.capabilitySupport?.[capability.value]
            );
          }
        });
        return buildRowsFromCollapsedGroup(finalizeCollapsedModelGroup(mergedGroup), baseGroup.displayName);
      }

      return variants.flatMap((variant) => {
        const subtitle = variant.vehicleType ? String(variant.vehicleType).trim().toUpperCase() : '';
        return buildRowsFromCollapsedGroup(variant, baseGroup.displayName, subtitle);
      });
    })
    .sort((a, b) => {
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;
      if (a.isCompatible === b.isCompatible) return 0;
      return a.isCompatible ? -1 : 1;
    });
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function renderCompatibilityRows(resultsBody, models, emptyMessage = 'No models found.', showMakePrefix = false) {
  if (!resultsBody) return;

  if (!models || models.length === 0) {
    resultsBody.innerHTML = `
      <tr>
        <td colspan="3" class="px-4 py-4 text-sm text-gray-500">${escapeHtml(emptyMessage)}</td>
      </tr>
    `;
    return;
  }

  const supportedPillClass = 'inline-flex items-center rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700';
  const unsupportedPillClass = 'inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-200';
  const unverifiedPillClass = 'inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700';

  resultsBody.innerHTML = models.map((model) => {
    const rowClass = '';
    const isNew = model.isNew === true || model.origin === COMPATIBILITY_ORIGIN.NHTSA_ONLY;
    const capabilitySupport = model.capabilitySupport || {};
    const supportCellContent = isNew
      ? `<span class="${unverifiedPillClass}">Unverified</span>`
      : COMPATIBILITY_CAPABILITIES.map((capability) => {
          const status = capabilitySupport[capability.value];
          const pillClass = status === true
            ? supportedPillClass
            : status === null
              ? unverifiedPillClass
              : unsupportedPillClass;
          return `<span class="${pillClass}">${escapeHtml(capability.label)}</span>`;
        }).join(' ');
    const modelLabel = showMakePrefix && model.makeName
      ? `${model.makeName} ${model.name}`
      : model.name;
    const modelSubtitle = String(model.subtitle || '').trim();
    const modelCellContent = modelSubtitle
      ? `
          <div class="leading-tight">
            <div class="text-sm text-gray-900">${escapeHtml(modelLabel)}</div>
            <div class="mt-1 text-xs uppercase tracking-[0.08em] text-gray-500">${escapeHtml(modelSubtitle)}</div>
          </div>
        `
      : `<div class="text-sm text-gray-900">${escapeHtml(modelLabel)}</div>`;

    return `
      <tr class="${rowClass}">
        <td class="px-4 py-3">${modelCellContent}</td>
        <td class="px-4 py-3 text-sm text-gray-600">${escapeHtml(model.years || '-')}</td>
        <td class="px-4 py-3 text-sm"><div class="flex flex-wrap gap-1.5">${supportCellContent}</div></td>
      </tr>
    `;
  }).join('');
}

function getYearOptionsForRows(rows) {
  const yearSet = new Set();
  const maxOpenYear = new Date().getFullYear() + 1;

  rows.forEach((row) => {
    const explicitYears = parseYearsText(row.years);
    explicitYears.forEach((year) => yearSet.add(year));

    if (row.isCompatible === true && Number.isFinite(row.openEndedFromYear)) {
      for (let year = row.openEndedFromYear; year <= maxOpenYear; year += 1) {
        yearSet.add(year);
      }
    }
  });

  return Array.from(yearSet)
    .sort((a, b) => b - a)
    .map((year) => String(year));
}

function rowSupportsYear(row, selectedYear) {
  if (!Number.isFinite(selectedYear)) return true;

  if (row.isCompatible === true && Number.isFinite(row.openEndedFromYear)) {
    return selectedYear >= row.openEndedFromYear;
  }

  const explicitYears = parseYearsText(row.years);
  return explicitYears.has(selectedYear);
}

function isConfirmedCompatibleRow(row) {
  return row.isCompatible === true;
}

function parseSelectedYear(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  if (!/^\d{4}$/.test(trimmed)) return null;
  const year = Number(trimmed);
  return Number.isFinite(year) ? year : null;
}

function sortCompatibilityRows(rows, includeMakeName = false) {
  const inputRows = Array.isArray(rows) ? rows : [];
  return [...inputRows].sort((a, b) => {
    if (includeMakeName) {
      const makeCompare = String(a.makeName || '').localeCompare(String(b.makeName || ''));
      if (makeCompare !== 0) return makeCompare;
    }

    const nameCompare = String(a.name || '').localeCompare(String(b.name || ''));
    if (nameCompare !== 0) return nameCompare;

    const aCompatible = isConfirmedCompatibleRow(a);
    const bCompatible = isConfirmedCompatibleRow(b);
    if (aCompatible === bCompatible) return 0;
    return aCompatible ? -1 : 1;
  });
}

function getOrderedMakesForDropdown(makes, region) {
  const priorityMakesForRegion = region === 'us' ? US_PRIORITY_MAKES : [];
  const priorityKeys = new Set(priorityMakesForRegion.map((make) => normalizeMakeMatchKey(make)));

  const priorityMakes = makes
    .filter((make) => priorityKeys.has(normalizeMakeMatchKey(make.name)))
    .sort((a, b) => a.name.localeCompare(b.name));

  const otherMakes = makes
    .filter((make) => !priorityKeys.has(normalizeMakeMatchKey(make.name)))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { priorityMakes, otherMakes };
}

async function fetchCompatibilityData(region) {
  const apiUrl = new URL(COMPATIBILITY_API_PATH, window.location.origin);
  apiUrl.searchParams.set('region', isCompatibilityRegion(region) ? region : resolveCompatibilityRegion());
  if (COMPATIBILITY_API_SUPPORTED_ONLY) {
    apiUrl.searchParams.set('supported', 'true');
  }

  const response = await fetch(apiUrl.toString(), { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Compatibility API request failed: ${response.status}`);
  }
  return await response.json();
}

async function initializeCompatibilitySection() {
  const regionSelect = document.getElementById('compatibility-region');
  const makeSelect = document.getElementById('compatibility-make');
  const yearSelect = document.getElementById('compatibility-year');
  const resultsBody = document.getElementById('compatibility-results-body');
  const tableScroll = document.getElementById('compatibility-table-scroll');
  const scrollHint = document.getElementById('compatibility-scroll-hint');

  if (!makeSelect || !yearSelect || !resultsBody) return;

  let activeRegion = resolveCompatibilityRegion();
  let makeRowsLookup = new Map();
  let allRows = [];

  if (regionSelect && isCompatibilityRegion(activeRegion)) {
    regionSelect.value = activeRegion;
  }

  function updateTableScrollHint() {
    if (!tableScroll || !scrollHint) return;
    const hasOverflow = tableScroll.scrollHeight > tableScroll.clientHeight + 1;
    const atBottom = tableScroll.scrollTop + tableScroll.clientHeight >= tableScroll.scrollHeight - 2;
    scrollHint.classList.toggle('hidden', !hasOverflow || atBottom);
  }

  function repopulateYearOptions(rows, selectedYearValue = '') {
    resetSelect(yearSelect, 'All years', false);
    const years = getYearOptionsForRows(rows);
    years.forEach((year) => {
      const option = document.createElement('option');
      option.value = year;
      option.textContent = year;
      yearSelect.appendChild(option);
    });
    if (selectedYearValue && years.includes(selectedYearValue)) {
      yearSelect.value = selectedYearValue;
    }
  }

  function applyFilters({ refreshYearOptions = false } = {}) {
    const selectedMakeName = String(makeSelect.value || '').trim();
    const selectedYearValueBeforeRefresh = String(yearSelect.value || '').trim();

    const makeRows = selectedMakeName
      ? (makeRowsLookup.get(selectedMakeName) || [])
      : allRows;

    if (refreshYearOptions) {
      repopulateYearOptions(makeRows, selectedYearValueBeforeRefresh);
    }

    const selectedYear = parseSelectedYear(yearSelect.value);
    const hasSelectedYear = Number.isFinite(selectedYear);
    let filteredRows = hasSelectedYear
      ? makeRows.filter((model) => rowSupportsYear(model, selectedYear))
      : makeRows;
    const hasScopedSelection = selectedMakeName !== '' || hasSelectedYear;
    if (!hasScopedSelection) {
      filteredRows = filteredRows.filter((model) => isConfirmedCompatibleRow(model));
    }
    const sortedRows = sortCompatibilityRows(filteredRows, !selectedMakeName);

    const emptyMessage = hasSelectedYear
      ? `No compatibility records for ${selectedYear}.`
      : 'No compatibility records found.';
    renderCompatibilityRows(resultsBody, sortedRows, emptyMessage, !selectedMakeName);
    if (tableScroll) {
      tableScroll.scrollTop = 0;
      tableScroll.scrollLeft = 0;
    }
    requestAnimationFrame(updateTableScrollHint);
  }

  function setLoadingState() {
    makeSelect.disabled = true;
    yearSelect.disabled = true;
    renderCompatibilityRows(resultsBody, [], 'Loading compatibility data...');
    requestAnimationFrame(updateTableScrollHint);
  }

  async function loadCompatibilityData(region, { preserveSelection = false } = {}) {
    const nextRegion = isCompatibilityRegion(region) ? region : resolveCompatibilityRegion();
    const previousMakeSelection = preserveSelection ? String(makeSelect.value || '').trim() : '';
    const previousYearSelection = preserveSelection ? String(yearSelect.value || '').trim() : '';

    activeRegion = nextRegion;
    if (regionSelect) {
      regionSelect.value = activeRegion;
      regionSelect.disabled = true;
    }
    setLoadingState();

    const compatibilityData = await fetchCompatibilityData(activeRegion);
    const makes = Array.isArray(compatibilityData.makes) ? compatibilityData.makes : [];
    if (makes.length === 0) {
      throw new Error('Compatibility data contains no supported makes');
    }
    makeRowsLookup = new Map(
      makes.map((make) => [
        make.name,
        collapseMakeModels(make.name, make.models).map((model) => ({
          ...model,
          makeName: make.name
        }))
      ])
    );
    allRows = Array.from(makeRowsLookup.values()).flat();

    resetSelect(makeSelect, 'All makes', false);
    const { priorityMakes, otherMakes } = getOrderedMakesForDropdown(makes, activeRegion);
    priorityMakes.forEach((make) => {
      const collapsedModels = makeRowsLookup.get(make.name) || [];
      const uniqueModelCount = new Set(collapsedModels.map((model) => model.name)).size;
      const option = document.createElement('option');
      option.value = make.name;
      option.textContent = `${make.name} (${uniqueModelCount})`;
      makeSelect.appendChild(option);
    });
    if (priorityMakes.length > 0 && otherMakes.length > 0) {
      const dividerOption = document.createElement('option');
      dividerOption.value = '__divider__';
      dividerOption.textContent = '──────────';
      dividerOption.disabled = true;
      makeSelect.appendChild(dividerOption);
    }
    otherMakes.forEach((make) => {
      const collapsedModels = makeRowsLookup.get(make.name) || [];
      const uniqueModelCount = new Set(collapsedModels.map((model) => model.name)).size;
      const option = document.createElement('option');
      option.value = make.name;
      option.textContent = `${make.name} (${uniqueModelCount})`;
      makeSelect.appendChild(option);
    });

    if (preserveSelection && previousMakeSelection && makeRowsLookup.has(previousMakeSelection)) {
      makeSelect.value = previousMakeSelection;
      repopulateYearOptions(makeRowsLookup.get(previousMakeSelection) || [], previousYearSelection);
    } else {
      repopulateYearOptions(allRows);
    }

    makeSelect.disabled = false;
    yearSelect.disabled = false;
    if (regionSelect) {
      regionSelect.disabled = false;
    }
    applyFilters();
  }

  makeSelect.addEventListener('change', () => {
    applyFilters({ refreshYearOptions: true });
  });

  yearSelect.addEventListener('change', () => {
    applyFilters();
  });

  if (regionSelect) {
    regionSelect.addEventListener('change', async () => {
      const selectedRegion = isCompatibilityRegion(regionSelect.value) ? regionSelect.value : resolveCompatibilityRegion();
      setStoredCompatibilityRegion(selectedRegion);
      try {
        await loadCompatibilityData(selectedRegion);
      } catch (error) {
        Sentry.captureException(error);
        console.error('Failed to update compatibility data for region:', error);
        makeSelect.disabled = true;
        yearSelect.disabled = true;
        regionSelect.disabled = false;
        renderCompatibilityRows(resultsBody, [], 'Compatibility data is temporarily unavailable.');
        requestAnimationFrame(updateTableScrollHint);
      }
    });
  }

  if (tableScroll) {
    tableScroll.addEventListener('scroll', updateTableScrollHint);
  }
  window.addEventListener('resize', updateTableScrollHint);

  try {
    await loadCompatibilityData(activeRegion, { preserveSelection: true });
  } catch (error) {
    Sentry.captureException(error);
    console.error('Failed to initialize compatibility data:', error);
    makeSelect.disabled = true;
    yearSelect.disabled = true;
    if (regionSelect) {
      regionSelect.disabled = false;
    }
    renderCompatibilityRows(resultsBody, [], 'Compatibility data is temporarily unavailable.');
    requestAnimationFrame(updateTableScrollHint);
  }
}

function initializeWaitlistFormTracking() {
  const form = document.querySelector('form');
  if (!form) return;

  form.addEventListener('submit', async (event) => {
    try {
      // Just a placeholder – add your actual logic here
      console.log('Form submitted');

      // Simulate error (for testing)
      // throw new Error("Test error on submit");

    } catch (error) {
      Sentry.captureException(error);
      console.error('Form submission error:', error);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initializeCompatibilitySection();
  initializeWaitlistFormTracking();
});
